import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import './Home.css';

/* ── Data ─────────────────────────────────── */
const stats = [
  { number: '10K+', label: 'Active Students' },
  { number: '500+', label: 'Expert Courses' },
  { number: '95%',  label: 'Success Rate' },
  { number: '24/7', label: 'Support Available' },
];

const services = [
  {
    icon: '📋',
    color: '#eff6ff',
    accent: '#2563eb',
    title: 'Personalised Learning Plans',
    desc: 'Adaptive study plans tailored to your strengths, weaknesses, and exam schedule for maximum results.',
  },
  {
    icon: '👨‍🏫',
    color: '#f5f3ff',
    accent: '#7c3aed',
    title: 'Experienced Tutors & Coaches',
    desc: 'Learn from seasoned educators with deep subject expertise and proven teaching methodologies.',
  },
  {
    icon: '📅',
    color: '#fff7ed',
    accent: '#ea580c',
    title: 'Flexible Scheduling',
    desc: 'Study at your own pace with on-demand video lectures and live sessions that fit your timetable.',
  },
];

const learningSolutions = [
  {
    tag: 'FOR EVERY STUDENT',
    title: 'Personalised Sessions for Every Subject',
    desc: 'Our platform adapts to each learner\'s pace and style. Get one-on-one attention through structured video modules, downloadable notes, and targeted practice sets that drive real results.',
    checks: ['Customised study paths', 'Subject-wise tracking', 'Instant doubt resolution'],
    image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600&q=80',
    imageAlt: 'Student studying with laptop',
    reverse: false,
    accent: '#2563eb',
  },
  {
    tag: 'FLEXIBLE LEARNING',
    title: 'Learn at a Pace That Works for You',
    desc: 'No rigid schedules. Access your courses anytime, replay difficult concepts, and track your daily learning streaks — all from mobile or desktop.',
    checks: ['Offline downloads', 'Progress checkpoints', 'Multi-device access'],
    image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=600&q=80',
    imageAlt: 'Student with tablet',
    reverse: true,
    accent: '#7c3aed',
  },
  {
    tag: 'INTERACTIVE LESSONS',
    title: 'Fun and Engaging Learning Tools',
    desc: 'Gamified quizzes, animated explainers, and interactive whiteboards make even the toughest topics approachable and enjoyable for every age group.',
    checks: ['Gamified quizzes', 'Animated explainers', 'Live whiteboard sessions'],
    image: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=600&q=80',
    imageAlt: 'Interactive learning',
    reverse: false,
    accent: '#ea580c',
  },
];

const testimonials = [
  {
    text: 'Ab Meri Baari completely changed how I prepare for exams. The video lectures are crystal-clear and the practice quizzes pushed me to improve every single day!',
    author: 'Priya Sharma',
    role: '10th Board Student',
    rating: 5,
    initials: 'PS',
    color: '#2563eb',
  },
  {
    text: "The analytics dashboard tells me exactly where I'm falling behind. With expert mentorship, I've gone from average marks to topping my class.",
    author: 'Arjun Patel',
    role: '12th Board Student',
    rating: 5,
    initials: 'AP',
    color: '#7c3aed',
  },
  {
    text: 'Unlike every other app I\'ve tried, this one actually focuses on understanding. The PDFs, notes, and live classes together make a massive difference.',
    author: 'Anjali Verma',
    role: 'MBA Student',
    rating: 5,
    initials: 'AV',
    color: '#ea580c',
  },
  {
    text: 'Flexible scheduling and offline videos mean I can study even without internet. Best investment I\'ve made for my board exam preparation.',
    author: 'Rahul Mishra',
    role: '10th Student',
    rating: 5,
    initials: 'RM',
    color: '#16a34a',
  },
  {
    text: 'The gamified quizzes make practice feel like a game. I actually look forward to studying now. My parents couldn\'t believe my improvement!',
    author: 'Sneha Gupta',
    role: 'Class 9 Student',
    rating: 5,
    initials: 'SG',
    color: '#0284c7',
  },
  {
    text: 'Expert mentorship sessions helped me crack my MBA entrance. The live whiteboard sessions for quantitative aptitude were outstanding.',
    author: 'Vikram Singh',
    role: 'MBA Aspirant',
    rating: 5,
    initials: 'VS',
    color: '#dc2626',
  },
];

const features = [
  { icon: '🎥', title: 'Video Lectures',        desc: 'High-quality recorded sessions from expert educators you can rewatch anytime.' },
  { icon: '📝', title: 'Smart Quizzes',          desc: 'Topic-wise quizzes designed to build accuracy and exam speed progressively.' },
  { icon: '📄', title: 'Study Materials',        desc: 'Download PDFs, notes, and revision sheets aligned to your syllabus.' },
  { icon: '📊', title: 'Performance Analytics',  desc: 'Detailed progress reports that highlight strengths and pinpoint weak areas.' },
  { icon: '🎯', title: 'Exam Preparation',       desc: 'Structured courses for board exams and competitive tests with proven results.' },
  { icon: '👨‍🏫', title: 'Expert Mentorship',     desc: 'One-on-one guidance from experienced teachers to keep you on track.' },
];

/* ── Intersection Observer Hook ──────────── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('revealed');
          observer.unobserve(el);
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

/* ── Reveal Wrapper ──────────────────────── */
function Reveal({ children, className = '', delay = 0, direction = 'up' }) {
  const ref = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal reveal-${direction} ${className}`}
      style={{ '--delay': `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════
   HOME COMPONENT
   ══════════════════════════════════════════ */
function Home() {
  return (
    <div className="home-root">

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-text">
            {/* ── App branding block (matches app splash screen) ── */}
            <Reveal className="hero-brand">
              <div className="hero-brand-icon-wrap">
                <div className="hero-brand-icon-glow" />
                <img src="/512-app-icon.png" alt="Ab Meri Baari" className="hero-brand-icon" />
              </div>
              <div className="hero-brand-name">Ab Meri Baari</div>
              <div className="hero-brand-tagline">Kyunki Padhna Zaroori hai...</div>
            </Reveal>

            <Reveal delay={100}>
              <span className="hero-eyebrow">🎓 India's #1 Edu Platform</span>
            </Reveal>
            <Reveal delay={180}>
              <p className="hero-desc">
                Learn with video lectures, practice quizzes, download PDFs, and track your progress —
                all in one powerful platform designed for every student.
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="hero-actions">
                <a href="#services" className="btn btn-primary btn-lg">Explore Courses</a>
                <Link to="/contact-us" className="btn btn-outline btn-lg">Get in Touch</Link>
              </div>
            </Reveal>
            <Reveal delay={320}>
              <div className="hero-trust">
                <div className="hero-trust-avatars">
                  {['P','A','R','+'].map((l, i) => <span key={i}>{l}</span>)}
                </div>
                <div className="hero-trust-text">
                  <strong>10,000+ Students</strong>
                  already learning on Ab Meri Baari
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal direction="left" className="hero-media">
            <div className="hero-image-wrap">
              <img
                src="https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=800&q=80"
                alt="Students learning"
              />
            </div>
            <div className="hero-badge hero-badge-1 float-badge">
              <div className="hero-badge-icon">📚</div>
              <div className="hero-badge-text">
                <strong>500+ Courses</strong>
                <span>Available now</span>
              </div>
            </div>
            <div className="hero-badge hero-badge-2 float-badge float-badge-delay">
              <div className="hero-badge-icon">⭐</div>
              <div className="hero-badge-text">
                <strong>4.9 / 5 Rating</strong>
                <span>From students</span>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Wave bottom */}
        <div className="hero-wave">
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,20 C320,60 780,0 1140,40 C1280,55 1380,30 1440,20 L1440,60 L0,60 Z" fill="var(--primary)" />
          </svg>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <div className="stats-bar">
        <div className="stats-bar-inner">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 80} className="stat-item">
              <div className="stat-number">{s.number}</div>
              <div className="stat-label">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* ════════════════════════════════════
          SERVICES SECTION
          ════════════════════════════════════ */}
      <section className="services-section" id="services">
        <div className="services-inner">
          <Reveal className="services-header text-center">
            <span className="section-label">Our Services</span>
            <h2 className="section-title">How Our Learning<br />Platform Works</h2>
            <p className="section-desc">
              Discover the features that make Ab Meri Baari the most effective learning platform
              for students across every grade and discipline.
            </p>
          </Reveal>

          <div className="services-grid">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 100} className="service-card">
                <div
                  className="service-card-icon"
                  style={{ background: s.color, color: s.accent }}
                >
                  {s.icon}
                </div>
                <h3 className="service-card-title">{s.title}</h3>
                <p className="service-card-desc">{s.desc}</p>
                <a href="#learn-more" className="service-card-link" style={{ color: s.accent }}>
                  Learn more →
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════
          EXPLORE LEARNING SOLUTIONS
          ════════════════════════════════════ */}
      <section className="solutions-section" id="solutions">
        <div className="solutions-inner">
          <Reveal className="solutions-header text-center">
            <span className="section-label">Learning Solutions</span>
            <h2 className="section-title">Explore Our Learning Solutions</h2>
            <p className="section-desc">
              Three pillars that work together to create a complete, outcome-driven education experience.
            </p>
          </Reveal>

          {learningSolutions.map((sol, i) => (
            <Reveal
              key={sol.title}
              delay={0}
              direction={sol.reverse ? 'right' : 'left'}
              className={`solution-row${sol.reverse ? ' solution-row--reverse' : ''}`}
            >
              {/* Image */}
              <div className="solution-media">
                <div
                  className="solution-img-wrap"
                  style={{ '--sol-accent': sol.accent }}
                >
                  <img src={sol.image} alt={sol.imageAlt} />
                  <div className="solution-img-badge">
                    <span style={{ background: sol.accent }}>✦</span>
                    {sol.tag}
                  </div>
                </div>
              </div>

              {/* Text */}
              <div className="solution-text">
                <span className="section-label" style={{ color: sol.accent }}>{sol.tag}</span>
                <h3 className="solution-title">{sol.title}</h3>
                <p className="solution-desc">{sol.desc}</p>
                <ul className="solution-checks">
                  {sol.checks.map((c) => (
                    <li key={c} style={{ '--check-color': sol.accent }}>
                      <span className="solution-check-dot" style={{ background: sol.accent }}>✓</span>
                      {c}
                    </li>
                  ))}
                </ul>
                <a href="#courses" className="btn btn-primary" style={{ background: sol.accent, borderColor: sol.accent }}>
                  Get Started →
                </a>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── FEATURES GRID ── */}
      <section className="features-section" id="courses">
        <div className="features-inner">
          <Reveal className="features-header text-center">
            <span className="section-label">Why Choose Us</span>
            <h2 className="section-title">Everything You Need to Succeed</h2>
            <p className="section-desc">
              Built for real learning outcomes — from concept clarity to exam-day confidence.
            </p>
          </Reveal>
          <div className="features-grid">
            {features.map((f, i) => (
              <Reveal key={f.title} delay={i * 60} className="feature-card">
                <div className="feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="how-section">
        <div className="how-inner">
          <Reveal className="how-header text-center">
            <span className="section-label">Simple Process</span>
            <h2 className="section-title">How It Works</h2>
            <p className="section-desc">
              Four simple steps to start your learning journey with Ab Meri Baari.
            </p>
          </Reveal>
          <div className="steps-grid">
            {[
              { num: '01', title: 'Sign Up',   desc: 'Create your free account in under 2 minutes.' },
              { num: '02', title: 'Learn',     desc: 'Watch lectures, read PDFs, and understand deeply.' },
              { num: '03', title: 'Practice',  desc: 'Take quizzes and tests to reinforce concepts.' },
              { num: '04', title: 'Improve',   desc: 'Track analytics and hit your target score.' },
            ].map((s, i) => (
              <Reveal key={s.num} delay={i * 100} className="step-card">
                <div className="step-num">{s.num}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════
          ENHANCED TESTIMONIALS
          ════════════════════════════════════ */}
      <section className="testimonials-section" id="testimonials">
        <div className="testimonials-inner">
          <Reveal className="testimonials-header text-center">
            <span className="section-label">Student Reviews</span>
            <h2 className="section-title">Listen to Our Students</h2>
            <p className="section-desc">
              Real stories from real students whose learning journeys were transformed by Ab Meri Baari.
            </p>
          </Reveal>

          {/* Top row — 3 featured large cards */}
          <div className="t-featured-grid">
            {testimonials.slice(0, 3).map((t, i) => (
              <Reveal key={t.author} delay={i * 100} className="t-featured-card">
                <div className="t-quote-mark">"</div>
                <div className="t-stars">
                  {'★'.repeat(t.rating)}
                </div>
                <p className="t-text">{t.text}</p>
                <div className="t-author">
                  <div className="t-avatar" style={{ background: t.color }}>{t.initials}</div>
                  <div>
                    <div className="t-name">{t.author}</div>
                    <div className="t-role">{t.role}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Bottom row — 3 compact cards */}
          <div className="t-compact-grid">
            {testimonials.slice(3).map((t, i) => (
              <Reveal key={t.author} delay={i * 80} className="t-compact-card">
                <div className="t-compact-header">
                  <div className="t-avatar t-avatar-sm" style={{ background: t.color }}>{t.initials}</div>
                  <div>
                    <div className="t-name">{t.author}</div>
                    <div className="t-role">{t.role}</div>
                  </div>
                  <div className="t-stars t-stars-sm">{'★'.repeat(t.rating)}</div>
                </div>
                <p className="t-text t-text-sm">"{t.text}"</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════
          APP DOWNLOAD SECTION
          ════════════════════════════════════ */}
      <section className="app-download-section" id="download-app">
        {/* Background decoration */}
        <div className="app-dl-bg-orb app-dl-orb-1" />
        <div className="app-dl-bg-orb app-dl-orb-2" />
        <div className="app-dl-bg-orb app-dl-orb-3" />

        <div className="app-dl-inner">

          {/* ── LEFT: Text + Buttons + QR ── */}
          <div className="app-dl-content">
            <Reveal>
              <span className="section-label" style={{ color: '#7c3aed' }}>📱 Mobile App</span>
              <h2 className="app-dl-headline">
                Take Your Learning<br />
                <span className="app-dl-headline-gradient">Anywhere, Anytime</span>
              </h2>
              <p className="app-dl-subtext">
                Download the Ab Meri Baari app and unlock offline access to video lectures,
                smart quizzes, personalised study plans, and live doubt sessions —
                all from the palm of your hand.
              </p>
            </Reveal>

            {/* Benefits pills */}
            <Reveal delay={100}>
              <div className="app-dl-benefits">
                {[
                  { icon: '📶', text: 'Offline Downloads' },
                  { icon: '🔔', text: 'Smart Reminders' },
                  { icon: '⚡', text: 'Instant Access' },
                  { icon: '🎯', text: 'Personalised Feed' },
                ].map((b) => (
                  <span key={b.text} className="app-dl-pill">
                    <span>{b.icon}</span>{b.text}
                  </span>
                ))}
              </div>
            </Reveal>

            {/* Store buttons */}
            <Reveal delay={160}>
              <div className="app-dl-store-btns">
                <a
                  href="https://play.google.com/store"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-store-btn"
                  aria-label="Get it on Google Play"
                >
                  <svg className="store-btn-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M3.18 23.76c.3.17.64.24.98.21l.13-.08 11.05-6.38-2.39-2.39-9.77 8.64zm-1.02-20.5A1.83 1.83 0 002 4.5v15c0 .44.13.86.36 1.21l.1.1 8.4-8.4v-.2L2.16 3.26zm15.6 8.72-2.99-1.73-2.68 2.68 2.68 2.68 3.01-1.74c.86-.49.86-1.4-.02-1.89zM4.16.24l-.14-.08C3.68-.01 3.31.05 3 .22L12.87 10.1l2.39-2.39L4.16.24z"/>
                  </svg>
                  <span className="store-btn-text">
                    <span className="store-btn-small">GET IT ON</span>
                    <span className="store-btn-large">Google Play</span>
                  </span>
                </a>

                <a
                  href="https://apps.apple.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-store-btn"
                  aria-label="Download on the App Store"
                >
                  <svg className="store-btn-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <span className="store-btn-text">
                    <span className="store-btn-small">DOWNLOAD ON THE</span>
                    <span className="store-btn-large">App Store</span>
                  </span>
                </a>
              </div>
            </Reveal>

            {/* QR Code + rating row */}
            <Reveal delay={220}>
              <div className="app-dl-qr-row">
                <div className="app-dl-qr-card">
                  <div className="app-dl-qr-frame" aria-label="QR code to download the app">
                    {/* SVG QR-style graphic */}
                    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="app-dl-qr-svg" aria-hidden="true">
                      {/* top-left finder */}
                      <rect x="5"  y="5"  width="30" height="30" rx="3" fill="#1e293b"/>
                      <rect x="10" y="10" width="20" height="20" rx="2" fill="#fff"/>
                      <rect x="15" y="15" width="10" height="10" rx="1" fill="#2563eb"/>
                      {/* top-right finder */}
                      <rect x="65" y="5"  width="30" height="30" rx="3" fill="#1e293b"/>
                      <rect x="70" y="10" width="20" height="20" rx="2" fill="#fff"/>
                      <rect x="75" y="15" width="10" height="10" rx="1" fill="#2563eb"/>
                      {/* bottom-left finder */}
                      <rect x="5"  y="65" width="30" height="30" rx="3" fill="#1e293b"/>
                      <rect x="10" y="70" width="20" height="20" rx="2" fill="#fff"/>
                      <rect x="15" y="75" width="10" height="10" rx="1" fill="#2563eb"/>
                      {/* data dots grid */}
                      {[
                        [42,5],[47,5],[52,5],[57,5],
                        [42,10],[52,10],[57,10],
                        [42,15],[47,15],[57,15],
                        [42,20],[47,20],[52,20],
                        [42,25],[52,25],[57,25],
                        [5,42],[10,42],[20,42],[25,42],[30,42],
                        [5,47],[15,47],[25,47],
                        [5,52],[10,52],[20,52],[30,52],
                        [5,57],[25,57],[30,57],
                        [40,40],[45,40],[55,40],[60,40],
                        [40,45],[50,45],[60,45],
                        [40,50],[45,50],[55,50],[60,50],
                        [40,55],[45,55],[55,55],
                        [40,60],[50,60],[60,60],
                        [65,40],[70,40],[80,40],[90,40],[95,40],
                        [65,45],[75,45],[85,45],[95,45],
                        [65,50],[70,50],[80,50],[90,50],
                        [65,55],[75,55],[85,55],[95,55],
                        [65,60],[70,60],[80,60],[90,60],[95,60],
                        [40,65],[50,65],[55,65],[60,65],
                        [40,70],[45,70],[55,70],
                        [40,75],[50,75],[60,75],
                        [40,80],[45,80],[55,80],[60,80],
                        [40,85],[45,85],[55,85],
                        [40,90],[50,90],[60,90],[65,90],[70,90],[80,90],[90,90],[95,90],
                        [65,70],[80,70],[85,70],
                        [65,75],[75,75],[85,75],[95,75],
                        [65,80],[75,80],[90,80],[95,80],
                        [65,85],[70,85],[80,85],[90,85],
                      ].map(([x, y], idx) => (
                        <rect key={idx} x={x} y={y} width="4" height="4" rx="0.8" fill="#1e293b"/>
                      ))}
                      {/* Center logo placeholder */}
                      <rect x="44" y="44" width="12" height="12" rx="3" fill="#2563eb"/>
                    </svg>
                    <div className="app-dl-qr-logo">
                      <img src="/512-app-icon.png" alt="Ab Meri Baari" />
                    </div>
                  </div>
                  <p className="app-dl-qr-hint">Scan to download</p>
                </div>

                <div className="app-dl-meta">
                  <div className="app-dl-rating">
                    <div className="app-dl-stars">{'★★★★★'}</div>
                    <div className="app-dl-rating-num">4.9 / 5</div>
                    <div className="app-dl-rating-count">12,000+ ratings</div>
                  </div>
                  <div className="app-dl-divider" />
                  <div className="app-dl-downloads">
                    <div className="app-dl-dl-num">50K+</div>
                    <div className="app-dl-dl-label">Downloads</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* ── RIGHT: Phone Mockups ── */}
          <Reveal direction="left" className="app-dl-mockups">
            <div className="app-dl-phones-wrap">
              {/* Glow ring behind phones */}
              <div className="app-dl-phones-glow" />

              {/* Back phone (slightly behind, angled) */}
              <div className="app-phone app-phone-back float-badge-delay">
                <div className="app-phone-frame">
                  <div className="app-phone-notch" />
                  <div className="app-phone-screen app-phone-screen-2">
                    <div className="mock-screen-2">
                      <div className="mock-topbar">
                        <span className="mock-topbar-title">My Courses</span>
                        <span className="mock-topbar-icon">🔔</span>
                      </div>
                      <div className="mock-course-card" style={{ '--cc': '#eff6ff', '--ca': '#2563eb' }}>
                        <span className="mock-cc-icon">📐</span>
                        <div>
                          <div className="mock-cc-title">Mathematics</div>
                          <div className="mock-cc-progress-bar"><div style={{ width: '72%', background: '#2563eb' }} /></div>
                          <div className="mock-cc-pct">72% complete</div>
                        </div>
                      </div>
                      <div className="mock-course-card" style={{ '--cc': '#f5f3ff', '--ca': '#7c3aed' }}>
                        <span className="mock-cc-icon">⚗️</span>
                        <div>
                          <div className="mock-cc-title">Science</div>
                          <div className="mock-cc-progress-bar"><div style={{ width: '45%', background: '#7c3aed' }} /></div>
                          <div className="mock-cc-pct">45% complete</div>
                        </div>
                      </div>
                      <div className="mock-course-card" style={{ '--cc': '#fff7ed', '--ca': '#ea580c' }}>
                        <span className="mock-cc-icon">📖</span>
                        <div>
                          <div className="mock-cc-title">English</div>
                          <div className="mock-cc-progress-bar"><div style={{ width: '89%', background: '#ea580c' }} /></div>
                          <div className="mock-cc-pct">89% complete</div>
                        </div>
                      </div>
                      <div className="mock-streak-row">
                        <span>🔥</span>
                        <div>
                          <div className="mock-streak-num">14-day streak!</div>
                          <div className="mock-streak-sub">Keep it going</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Front phone (main, floating) */}
              <div className="app-phone app-phone-front float-badge">
                <div className="app-phone-frame">
                  <div className="app-phone-notch" />
                  <div className="app-phone-screen">
                    <div className="mock-screen-1">
                      {/* Header */}
                      <div className="mock-header">
                        <div className="mock-header-avatar">A</div>
                        <div>
                          <div className="mock-header-hi">Good Morning!</div>
                          <div className="mock-header-name">Arjun 👋</div>
                        </div>
                        <div className="mock-header-badge">🔔</div>
                      </div>
                      {/* Banner */}
                      <div className="mock-banner">
                        <div className="mock-banner-label">Today's Goal</div>
                        <div className="mock-banner-title">Complete Chapter 5</div>
                        <div className="mock-banner-progress">
                          <div className="mock-banner-bar">
                            <div className="mock-banner-fill" style={{ width: '65%' }} />
                          </div>
                          <span>65%</span>
                        </div>
                      </div>
                      {/* Quick stats */}
                      <div className="mock-stats-row">
                        <div className="mock-stat-chip" style={{ background: '#eff6ff' }}>
                          <span>📚</span>
                          <div>
                            <div className="mock-stat-n" style={{ color: '#2563eb' }}>12</div>
                            <div className="mock-stat-l">Lessons</div>
                          </div>
                        </div>
                        <div className="mock-stat-chip" style={{ background: '#f5f3ff' }}>
                          <span>🏆</span>
                          <div>
                            <div className="mock-stat-n" style={{ color: '#7c3aed' }}>840</div>
                            <div className="mock-stat-l">Points</div>
                          </div>
                        </div>
                        <div className="mock-stat-chip" style={{ background: '#fff7ed' }}>
                          <span>⏱</span>
                          <div>
                            <div className="mock-stat-n" style={{ color: '#ea580c' }}>2h</div>
                            <div className="mock-stat-l">Today</div>
                          </div>
                        </div>
                      </div>
                      {/* Recent */}
                      <div className="mock-section-label">Resume Learning</div>
                      <div className="mock-video-card">
                        <div className="mock-video-thumb">
                          <div className="mock-play-btn">▶</div>
                        </div>
                        <div className="mock-video-info">
                          <div className="mock-video-title">Quadratic Equations</div>
                          <div className="mock-video-meta">Math · 18 min left</div>
                          <div className="mock-video-bar">
                            <div style={{ width: '40%' }} />
                          </div>
                        </div>
                      </div>
                      {/* Bottom nav */}
                      <div className="mock-nav">
                        <span className="mock-nav-item active">🏠</span>
                        <span className="mock-nav-item">📚</span>
                        <span className="mock-nav-item">📊</span>
                        <span className="mock-nav-item">👤</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Reflection shine */}
                <div className="app-phone-shine" />
              </div>

              {/* Floating mini badges */}
              <div className="app-dl-float-chip app-dl-chip-1 float-badge">
                <span className="chip-icon">🎓</span>
                <div>
                  <div className="chip-title">New Lesson!</div>
                  <div className="chip-sub">Trigonometry · Ch.4</div>
                </div>
              </div>
              <div className="app-dl-float-chip app-dl-chip-2 float-badge-delay">
                <span className="chip-icon">🏆</span>
                <div>
                  <div className="chip-title">Top Student</div>
                  <div className="chip-sub">This week's rank #1</div>
                </div>
              </div>
              <div className="app-dl-float-chip app-dl-chip-3 float-badge">
                <span className="chip-icon">📥</span>
                <div>
                  <div className="chip-title">Downloaded</div>
                  <div className="chip-sub">12 lessons offline</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section">
        <div className="cta-inner">
          <Reveal className="cta-text">
            <h2>Ready to Start Learning?</h2>
            <p>Join thousands of students already learning on Ab Meri Baari today!</p>
          </Reveal>
          <Reveal delay={120} className="cta-actions">
            <a href="#signup" className="btn btn-white btn-lg pulse-btn">Sign Up Now via App</a>
            <Link
              to="/contact-us"
              className="btn btn-lg"
              style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
            >
              Contact Us
            </Link>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Home;
